import { ContentHeader, IconArrowRightBold, Tooltip } from '@croquiscom-pvt/zds';
import { vars } from '@croquiscom-pvt/zds/tokens';
import styled from '@emotion/styled';
import { SwiperSlide } from 'swiper/react';

import { Log } from '@common/log-manager';
import { ProductCardCarousel } from '@common/product-card';
import {
  MoreButton as ProductCarouselMoreButton,
  ProductGroupComponentType,
  useProductCardEvents,
} from '@domains/storehome-common';

import { useProductGroupService } from '../services/useProductGroupService';
import { getObjectSection } from '../utils/getObjectSection';

interface Props {
  log?: Partial<Log>;
  shop_id: string;
  category_id?: string;
  component: ProductGroupComponentType;
}

export const ProductGroupContainer = ({ log, shop_id, category_id, component }: Props) => {
  const {
    main_title,
    product_carousel: { more_button: product_carousel_more_button, item_column_count },
    more_button,
    title_icon_button,
    background_color,
  } = component;
  const {
    slide_width,
    carousel_padding_bottom,
    product_card_list,
    content_header_size,
    handleMoreButtonClick,
    handleCarouselMoreButtonClick,
  } = useProductGroupService({
    shop_id,
    category_id,
    component,
  });
  const { onClick, onImpression, onLikeClick } = useProductCardEvents();

  return (
    <Wrap backgroundColor={background_color?.normal} paddingBottom={carousel_padding_bottom}>
      <ContentHeader
        size={content_header_size}
        title={
          <Title>
            {main_title?.text ?? ''}
            {title_icon_button && (
              <Tooltip
                content={title_icon_button.text?.text}
                openTrigger='click'
                placement='bottom'
                containerMargin={10}
              >
                <IconWrapper>
                  <TitleIcon src={title_icon_button.image?.webp_url?.normal ?? title_icon_button.image?.url?.normal} />
                </IconWrapper>
              </Tooltip>
            )}
          </Title>
        }
        right={
          more_button
            ? {
                type: 'text-button',
                icon: IconArrowRightBold,
                text: more_button.text,
                onClick: () => handleMoreButtonClick(),
              }
            : undefined
        }
      />
      <ProductCardCarousel.Root
        navigation={product_card_list.length > 3}
        slidesPerView={item_column_count as ProductCardCarousel.RootProps['slidesPerView']}
      >
        <ProductCardCarousel.VerticalProductCardSlideItems
          items={product_card_list}
          onClick={(item, index, event) =>
            onClick(
              event,
              item,
              index,
              log && {
                ...log,
                ...(category_id && { category_id }),
                object_idx: index,
                object_section: getObjectSection(component.contents_type),
              },
            )
          }
          onImpression={(item, index) =>
            onImpression(
              item,
              index,
              log && {
                ...log,
                ...(category_id && { category_id }),
                object_idx: index,
                object_section: getObjectSection(component.contents_type),
              },
            )
          }
          onLikeClick={(is_liked, item, index) =>
            onLikeClick(
              is_liked,
              item,
              index,
              log && {
                ...log,
                ...(category_id && { category_id }),
                object_idx: index,
                object_section: getObjectSection(component.contents_type),
              },
            )
          }
        />
        {product_carousel_more_button && (
          <SwiperSlide style={{ width: slide_width, minWidth: 96 }}>
            <ProductCarouselMoreButton
              text={product_carousel_more_button.text}
              onClick={() => handleCarouselMoreButtonClick()}
            />
          </SwiperSlide>
        )}
      </ProductCardCarousel.Root>
    </Wrap>
  );
};

const Wrap = styled.section<{ backgroundColor?: string | null; paddingBottom?: number }>`
  position: relative;
  background-color: ${({ backgroundColor }) => backgroundColor ?? vars.color.backgroundSurface};
  padding-bottom: ${({ paddingBottom }) => paddingBottom ?? 20}px;
`;

const Title = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
  position: relative;
`;

const IconWrapper = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const TitleIcon = styled.img`
  width: 20px;
  height: 20px;
`;
